import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
`

const RatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.product.exclusive.background};
  color: white;
  border-radius: ${rem(4)};
  font-size: ${rem(12)};
  font-weight: 600;
  padding: ${rem(2)} ${rem(6)};
  min-width: ${rem(28)};
`

const RatingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(12)};
`

const RATING_OPTIONS = [
  { value: 9, label: '9.0' },
  { value: 8, label: '8.0' },
  { value: 7, label: '7.0' },
]

interface Props {
  ratingCounts?: Record<number, number>
  compact?: boolean
  hideCounts?: boolean
}

function ExperienceSearchRatingFilter({ ratingCounts, compact = false, hideCounts = false }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()

  const isAllSelected = !searchFilters.minRating

  const allLabel = useMemo(() => intl.formatMessage({
    id: '4AnY0LVHfJOfBTaRm8Sz',
    defaultMessage: 'All',
    description: 'Checkbox label to select all filter options in the filters panel',
  }), [intl])

  const orAboveLabel = useMemo(() => intl.formatMessage({
    id: '_u_PESnw7GIdW8U9jJ7Z',
    defaultMessage: 'or above',
    description: 'Suffix text displayed with each minimum rating option in experience search filters',
  }), [intl])

  const onRatingChange = useCallback((rating: number | undefined) => {
    trackSearchFilter({
      label: rating ? `${rating}.0+ ${orAboveLabel}` : allLabel,
      type: 'customer_rating',
      value: rating ?? 'all',
    })
    setSearchFilters({
      ...searchFilters,
      minRating: rating,
    })
  }, [allLabel, orAboveLabel, searchFilters, setSearchFilters, trackSearchFilter])

  const compactItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    return [
      {
        value: 'all',
        label: allLabel,
        checked: isAllSelected,
      },
      ...RATING_OPTIONS.map((option) => ({
        value: String(option.value),
        label: <RatingLabel><RatingBadge>{option.label}</RatingBadge>{orAboveLabel}</RatingLabel>,
        checked: searchFilters.minRating === option.value,
        count: ratingCounts?.[option.value],
        hideCount: hideCounts,
      })),
    ]
  }, [allLabel, hideCounts, isAllSelected, orAboveLabel, ratingCounts, searchFilters.minRating])

  const onCompactRatingChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    onRatingChange(value === 'all' ? undefined : Number(value))
  }, [onRatingChange])

  if (compact) {
    return (
      <FilterPanelCheckboxGroup
        name="minRating"
        items={compactItems}
        onChange={onCompactRatingChange}
      />
    )
  }

  return (
    <ListContainer>
      <CheckboxInputListItem
        inputPlacement="start"
        checked={isAllSelected}
        onChange={() => onRatingChange(undefined)}
        title={allLabel}
        size="medium"
      />
      {RATING_OPTIONS.map(option => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={searchFilters.minRating === option.value}
          onChange={() => onRatingChange(searchFilters.minRating === option.value ? undefined : option.value)}
          title={<RatingLabel><RatingBadge>{option.label}</RatingBadge>{orAboveLabel}</RatingLabel>}
          size="medium"
        />
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchRatingFilter
